class StoreCardSearch extends HTMLElement {
  #controller;
  #events;
  #timer;
  #items = [];
  #shown = 0;
  #visibleItems = [];
  #input;

  connectedCallback() {
    this.#events = new AbortController();
    const { signal } = this.#events;
    this.querySelector('[data-card-stock]')?.addEventListener('change', () => this.#applyStock(), { signal });
    this.querySelector('[data-card-more]')?.addEventListener('click', () => this.#showMore(), { signal });
    this.querySelector('[data-card-related]')?.addEventListener('click', () => {
      this.dataset.showRelated = 'true';
      this.dataset.hasMatches = 'false';
      this.hidden = true;
      this.querySelector('[data-card-related]').hidden = true;
      this.closest('.shopify-section')?.querySelector('results-list')?.scrollIntoView({ block: 'start' });
    }, { signal });
    if (this.dataset.mode === 'modal') {
      this.#input = this.closest('form')?.querySelector('input[name="q"]');
      this.#input?.addEventListener('input', () => {
        clearTimeout(this.#timer);
        this.#controller?.abort();
        this.dataset.hasMatches = 'false';
        this.querySelector('[data-card-list]').replaceChildren();
        this.#timer = setTimeout(() => this.search(this.#input.value), 350);
      }, { signal });
    } else {
      // Native filters retain their exact native scope and behaviour.
      if ([...new URL(location.href).searchParams.keys()].some(key => key.startsWith('filter.'))) return;
      this.search(this.dataset.query || '');
    }
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.reset();
  }

  reset() {
    clearTimeout(this.#timer);
    this.#controller?.abort();
    this.hidden = true;
    this.dataset.hasMatches = 'false';
    this.querySelector('[data-card-list]')?.replaceChildren();
    this.#items = [];
    this.#visibleItems = [];
  }

  async search(rawQuery) {
    this.reset();
    const query = rawQuery.trim().slice(0, 100).replace(/\bmagic:\s*the\s+gathering\b/gi, 'Magic the Gathering');
    if (query.length < 2 || /[:*"()]/.test(query)) return;
    const controller = new AbortController();
    this.#controller = controller;
    this.hidden = false;
    this.dataset.showRelated = 'false';
    this.querySelector('[data-card-more]').hidden = true;
    const related = this.querySelector('[data-card-related]');
    if (related) related.hidden = true;
    const status = this.querySelector('[data-card-status]');
    status.textContent = this.dataset.loading;
    this.setAttribute('aria-busy', 'true');
    try {
      const getPage = async page => {
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('view', 'card-matches');
        url.searchParams.set('page', String(page));
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Card search unavailable');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = document.querySelector('[data-card-search-page]');
        if (!root) throw new Error('Card search view unavailable');
        return { pages: Number(root.dataset.pages), items: [...root.querySelectorAll('.store-card-match')] };
      };
      const first = await getPage(1);
      const items = [...first.items];
      // Two requests at a time, only after a shopper searches. Nothing loads on browsing pages.
      for (let page = 2; page <= first.pages; page += 2) {
        const pages = [getPage(page)];
        if (page + 1 <= first.pages) pages.push(getPage(page + 1));
        const results = await Promise.all(pages);
        results.forEach(result => items.push(...result.items));
      }
      if (controller.signal.aborted) return;
      const unique = new Map(items.map(item => [item.dataset.cardId, item]));
      this.#items = [...unique.values()].sort((a, b) => {
        const availability = Number(b.dataset.available === 'true') - Number(a.dataset.available === 'true');
        return availability || a.dataset.title.localeCompare(b.dataset.title, undefined, { numeric: true });
      });
      this.#shown = 0;
      this.dataset.hasMatches = String(this.#items.length > 0);
      status.textContent = this.#items.length ? this.dataset.count.replace('__COUNT__', String(this.#items.length)) : this.dataset.empty;
      this.#applyStock();
      if (related) related.hidden = this.#items.length === 0;
    } catch (error) {
      if (!controller.signal.aborted) status.textContent = this.dataset.error;
    } finally {
      if (!controller.signal.aborted) this.removeAttribute('aria-busy');
    }
  }

  #applyStock() {
    this.#visibleItems = this.querySelector('[data-card-stock]')?.checked ? this.#items.filter(item => item.dataset.available === 'true') : this.#items;
    this.#shown = 0;
    this.querySelector('[data-card-list]').replaceChildren();
    this.querySelector('[data-card-status]').textContent = this.#visibleItems.length ? this.dataset.count.replace('__COUNT__', String(this.#visibleItems.length)) : (this.#items.length ? this.dataset.noStock : this.dataset.empty);
    this.#showMore();
  }

  #showMore() {
    const batch = this.dataset.mode === 'modal' ? 8 : 24;
    const next = Math.min(this.#shown + batch, this.#visibleItems.length);
    this.querySelector('[data-card-list]').append(...this.#visibleItems.slice(this.#shown, next));
    this.#shown = next;
    this.querySelector('[data-card-more]').hidden = next >= this.#visibleItems.length;
  }
}
if (!customElements.get('store-card-search')) customElements.define('store-card-search', StoreCardSearch);

